import React, { useState, useEffect } from "react";
import { FaTimes, FaCalendarAlt, FaUpload } from "react-icons/fa";
import { CreateOutgoingRequests, GetMonazamBankAccountsForHGO } from "../../../../Actions/RequestActions";
import { SuccessToast, WarningToast } from "../../../../Util";
import { Spinner } from "react-bootstrap";
import "./OutgoingRequest.css";

const defaultValues = {
  date: "",
  narration: "",
  amount: "",
  currency: "",
  categoryType: "",
  letterOfConsent: null,
  bankName: "",
  monazamBankAccountId: "",
};

const OutgoingRequest = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(defaultValues);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      GetMonazamBankAccountsForHGO(
        (data) => {
          if (data?.bankAccounts) {
            setBankAccounts(data.bankAccounts);
          }
        },
        setBankLoading,
        setBankError
      );
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "amount") {
      let cleanValue = value.replace(/[^0-9.]/g, "");
      cleanValue = cleanValue.replace(/(\..*?)\..*/g, "$1");
      cleanValue = cleanValue.replace(/(\.\d{2})\d+/, "$1");
      setFormData((prevData) => ({
        ...prevData,
        [name]: cleanValue,
      }));
    } else if (name === "letterOfConsent") {
      if (files && files[0]) {
        const file = files[0];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (fileExtension !== 'pdf') {
          WarningToast("Only PDF files are allowed for Letter of Consent");
          return;
        }
        setFormData((prevData) => ({
          ...prevData,
          [name]: file,
        }));
      }
    } else {
      setFormData((prevData) => {
        const newData = {
          ...prevData,
          [name]: value,
        };
        
        // Handle categoryType changes
        if (name === "categoryType") {
          // Auto-set SAR currency for OpapTransfer
          if (value === "OpapTransfer") {
            newData.currency = "SAR";
            newData.bankName = "";
            newData.monazamBankAccountId = "";
          } else {
            newData.currency = "";
            newData.bankName = "";
            newData.monazamBankAccountId = "";
          }
        }
        else if (name === "currency") {
          newData.bankName = "";
          newData.monazamBankAccountId = "";
        }
        else if (name === "bankName" && prevData.currency === "PKR") {
          const selectedAccount = bankAccounts.find(
            acc => acc.accountType === "PKR" && (acc.bankName === value || acc.accountTitle === value)
          );
          if (selectedAccount && selectedAccount.id) {
            newData.monazamBankAccountId = selectedAccount.id;
          } else {
            newData.monazamBankAccountId = "";
          }
        }
        
        return newData;
      });
    }
    setValidationErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleCreate = () => {
    SuccessToast("Outgoing request created successfully!");
    setFormData(defaultValues);
    setValidationErrors({});
    onClose("create");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    const { date, narration, currency, categoryType, amount, letterOfConsent } = formData;

    if (!date) errors.date = true;
    if (!narration?.trim()) errors.narration = true;
    if (!currency) errors.currency = true;
    if (!categoryType) errors.categoryType = true;
    if (!amount || Number(amount) <= 0) errors.amount = true;
    
    if (
      categoryType !== "OpapTransfer" &&
      currency === "PKR" &&
      !formData.monazamBankAccountId
    ) {
      errors.bankName = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      const currentTime = new Date();
      const hours = currentTime.getHours().toString().padStart(2, '0');
      const minutes = currentTime.getMinutes().toString().padStart(2, '0');
      const seconds = currentTime.getSeconds().toString().padStart(2, '0');
      
      const dateTimeString = `${date}T${hours}:${minutes}:${seconds}.000Z`;
      
      const submitFormData = new FormData();
      submitFormData.append('date', dateTimeString);
      submitFormData.append('narration', narration.trim());
      submitFormData.append('currency', currency);
      submitFormData.append('amount', Number(amount));
      submitFormData.append('type', 'outgoing');
      submitFormData.append('categoryType', categoryType);
      
      if (
        currency === "PKR" &&
        categoryType !== "OpapTransfer" &&
        formData.monazamBankAccountId
      ) {
        submitFormData.append('monazamBankAccountId', formData.monazamBankAccountId);
      }
      
      if (letterOfConsent) {
        submitFormData.append('file', letterOfConsent);
      }

      setSubmitting(true);
      CreateOutgoingRequests(submitFormData, (error) => {
        setSubmitting(false);
        setMessage(error);
      }, () => {
        setSubmitting(false);
        handleCreate();
      });
    } else {
      WarningToast("All fields are required.");
    }
  };

  useEffect(() => {
    if (message && message.type === "error") {
      setSubmitting(false);
    }
  }, [message]);

  if (!isOpen) return <></>;
  else
    return (
      <>
        <style>
          {`
            .custom-date-input::-webkit-calendar-picker-indicator {
              display: none !important;
            }
            .custom-date-input::-webkit-inner-spin-button,
            .custom-date-input::-webkit-outer-spin-button {
              display: none !important;
            }
            .custom-date-input::-ms-clear {
              display: none !important;
            }
          `}
        </style>
        <div className="modal d-block modal-blur-bg outgoing-request-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
            <button
              className="btn modal-label-txt modal-btn-bg btn-success position-absolute top-0 end-0 m-3"
              onClick={() => {
                onClose("close");
              }}
            >
              Close <FaTimes className="me-1" />
            </button>
            <div className="modal-header">
              <h2 className="modal-title mx-auto modal-heading-txt">
                Outgoing Request
              </h2>
            </div>
            <div className="modal-body px-5">
              <div className="form-row">
                <div className="form-group">
                  <label className="modal-label-txt">Date <span style={{ color: 'red' }}>*</span></label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      className="form-control modal-input-txt custom-date-input"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      onClick={(e) => e.target.showPicker()}
                    />
                    <FaCalendarAlt className="calendar-icon" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="modal-label-txt">Type <span style={{ color: 'red' }}>*</span></label>
                  <select
                    className="form-select modal-input-txt"
                    name="categoryType"
                    value={formData.categoryType}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="MonazamTransfer">
                      Monazam to OPAP Transfer
                    </option>
                    <option value="MonazamToEhajjTransfer">
                      Monazam to E Hajj Transfer
                    </option>
                    <option value="OpapTransfer">
                      Opap to E Hajj Transfer
                    </option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="modal-label-txt">Currency <span style={{ color: 'red' }}>*</span></label>
                  {(() => {
                    if (formData.categoryType === "OpapTransfer") {
                      return (
                        <input
                          type="text"
                          className="form-control modal-input-txt"
                          value="SAR"
                          readOnly
                        />
                      );
                    }
                    
                    const allowedCurrencies = 
                      formData.categoryType === "MonazamTransfer" ||
                      formData.categoryType === "MonazamToEhajjTransfer"
                        ? ["PKR", "USD"]
                        : [];
                    
                    return (
                      <select
                        className="form-select modal-input-txt"
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        disabled={!formData.categoryType}
                      >
                        <option value="">Select</option>
                        {allowedCurrencies.map((curr) => (
                          <option key={curr} value={curr}>
                            {curr}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                </div>

                {formData.categoryType !== "OpapTransfer" &&
                 (formData.categoryType === "MonazamTransfer" || formData.categoryType === "MonazamToEhajjTransfer") &&
                 formData.currency === "PKR" && (
                  <div className="form-group">
                    <label className="modal-label-txt">Monazzam Account <span style={{ color: 'red' }}>*</span></label>
                    <select
                      className="form-select modal-input-txt"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      disabled={bankLoading || !formData.currency}
                    >
                      <option value="" disabled hidden>
                        {bankLoading ? "Loading accounts..." : "Select"}
                      </option>
                      {bankAccounts
                        .filter(acc => acc.accountType === "PKR")
                        .map((acc) => (
                          <option
                            key={acc.id}
                            value={acc.bankName || acc.accountTitle}
                          >
                            {acc.bankName || acc.accountTitle}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                
                {!(formData.categoryType !== "OpapTransfer" &&
                 (formData.categoryType === "MonazamTransfer" || formData.categoryType === "MonazamToEhajjTransfer") &&
                 formData.currency === "PKR") && (
                  <div className="form-group"></div>
                )}
              </div>

              <div className="form-group full-width">
                <label className="modal-label-txt">Amount <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  className="form-control modal-input-txt"
                  placeholder="20,555"
                  name="amount"
                  value={formData.amount ? Number(formData.amount).toLocaleString() : ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group full-width">
                <label className="modal-label-txt">Narration <span style={{ color: 'red' }}>*</span></label>
                <textarea
                  className="form-control modal-input-txt"
                  rows="3"
                  placeholder="Comments"
                  name="narration"
                  value={formData.narration}
                  onChange={handleChange}
                ></textarea>
              </div>

              <div className="form-group full-width">
                <label className="modal-label-txt">Letter of Consent (Optional)</label>
                <div 
                  className="file-upload-area"
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <FaUpload className="upload-icon" />
                  <span className="upload-text">Upload</span>
                  <input
                    type="file"
                    id="fileInput"
                    name="letterOfConsent"
                    onChange={handleChange}
                    accept=".pdf"
                    className="d-none"
                  />
                </div>
                {formData.letterOfConsent && (
                  <small className="text-muted mt-2 d-block">
                    Selected: {formData.letterOfConsent.name}
                  </small>
                )}
              </div>
            </div>

            <div className="modal-footer justify-content-center">
                <button
                  className="btn submit-btn modal-label-txt d-flex justify-content-center align-items-center"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Submit"
                  )}
              </button>
            </div>
            {message && (
              <div
                className={`alert mt-3 ${
                  message.type === "success" ? "alert-success" : "alert-danger"
                }`}
              >
                {message.text}
              </div>
            )}
          </div>
        </div>
      </div>
      </>
    );
};

export default OutgoingRequest;
